import React, { lazy, Suspense } from 'react';
import {
    HashRouter as Router,
    Navigate,
    Outlet,
    Route,
    Routes,
} from 'react-router-dom';
import SessionPolicy from './Middlewares/SessionPolicy';
import ScrollToTop from '@shared/Helpers/ScrolToTop';
import { useGoogleAnalytics } from '@shared/Hooks/GoogleAnalisicts/GooglAnalistics';
import ToastNotifications from '@shared/Widgets/ToastNotifications/ToastNotifications';
import '@styles/Container.css';
import { ROUTES } from '@configs/AppRoutes';
import LandingPage from '@modules/Landing/Pages/LandingPage';
import ProtectedRoute from './Middlewares/ProtectedRoute';
import BillOfMaterialsPage from '@modules/BillOfMaterials/Pages/BillOfMaterialsPage';
import { BasicLoader } from '@shared/Ui/Loaders/BasicLoader/BasicLoader';
import StepsConditionalProtectedRoute from './Middlewares/StepsConditionalProtectedRoute';

const TemplatesViewPage = lazy(
    () => import('@modules/BillOfMaterials/Pages/TemplatesViewPage')
);
const Step1Page = lazy(() => import('@modules/Steps/Pages/Step1Page'));
const Step2Page = lazy(() => import('@modules/Steps/Pages/Step2Page'));
const Step2SelectItemsPage = lazy(
    () => import('@modules/Steps/Pages/Step2SelectItemsPage')
);
const Step3Page = lazy(() => import('@modules/Steps/Pages/Step3Page'));
const TemplateCartPage = lazy(
    () => import('@modules/BillOfMaterials/Pages/TemplateCartPage')
);
const TemplateInfoPage = lazy(
    () => import('@modules/BillOfMaterials/Pages/TemplateInfoPage')
);

const App: React.FC = () => {
    useGoogleAnalytics();
    return (
        <div className="container">
            <Router>
                {/*<LiveHelpNowChatContainer />*/}
                <ScrollToTop />
                <SessionPolicy>
                    <Suspense fallback={<BasicLoader />}>
                        <Routes>
                            <Route
                                path={ROUTES.ROOT}
                                element={<Navigate to={ROUTES.BOM} replace />}
                            />
                            <Route
                                path={ROUTES.HOME}
                                element={<LandingPage />}
                            />
                            <Route
                                path={ROUTES.TEMPLATES}
                                element={<TemplatesViewPage />}
                            />
                            <Route
                                path={ROUTES.BOM}
                                element={
                                    <ProtectedRoute>
                                        <BillOfMaterialsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ROUTES.TEMPLATE_CART.path}
                                element={
                                    <ProtectedRoute requireProject={false}>
                                        <TemplateCartPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ROUTES.PROJECT_ASSISTANCE_LAYOUT}
                                element={<Outlet />}
                            >
                                <Route
                                    path={ROUTES.STEP1.path}
                                    element={
                                        <StepsConditionalProtectedRoute>
                                            <Step1Page />
                                        </StepsConditionalProtectedRoute>
                                    }
                                />
                                <Route
                                    path={ROUTES.STEP2.path}
                                    element={
                                        <StepsConditionalProtectedRoute>
                                            <Step2Page />
                                        </StepsConditionalProtectedRoute>
                                    }
                                />
                                <Route
                                    path={ROUTES.STEP2_SELECT_ITEMS.path}
                                    element={
                                        <StepsConditionalProtectedRoute>
                                            <Step2SelectItemsPage />
                                        </StepsConditionalProtectedRoute>
                                    }
                                />
                                <Route
                                    path={ROUTES.STEP3.path}
                                    element={
                                        <StepsConditionalProtectedRoute>
                                            <Step3Page />
                                        </StepsConditionalProtectedRoute>
                                    }
                                />
                            </Route>
                            <Route
                                path={ROUTES.TEMPLATE_INFO.path}
                                element={<TemplateInfoPage />}
                            />
                            <Route
                                path={ROUTES.NOT_FOUND}
                                element={<Navigate to={ROUTES.BOM} replace />}
                            />
                        </Routes>
                    </Suspense>
                </SessionPolicy>
                <ToastNotifications />
            </Router>
        </div>
    );
};

export default App;
